import { useEffect, useState, type DependencyList, type RefObject } from 'react';

/**
 * True while a scroll box has content hidden below its fold (QA-W4-8: the
 * settings sections of the dialer menu sat under the Power Hour card with no
 * hint). Re-read on scroll, on resize and when `deps` change. On mount, the
 * box's `[aria-current]` element (the current settings section) is brought
 * into view.
 */
export function useScrollCue(ref: RefObject<HTMLElement | null>, deps: DependencyList = []): boolean {
  const [moreBelow, setMoreBelow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    const current = el.querySelector<HTMLElement>('[aria-current="true"]');
    if (current && typeof current.scrollIntoView === 'function') current.scrollIntoView({ block: 'nearest' });
    read();
    el.addEventListener('scroll', read, { passive: true });
    window.addEventListener('resize', read);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', read);
      window.removeEventListener('resize', read);
      ro?.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
  return moreBelow;
}
